import { createContext, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import type { ReactNode } from "react";

const SIGNED_URL_REFRESH_MS = 10 * 60 * 1000;

// Signed media URLs expire after 15 minutes on the server, so they are fetched again before
// that, also on a form left open, not only when it is reopened.
export const SIGNED_MEDIA_URL_QUERY_TIMING = {
  staleTime: SIGNED_URL_REFRESH_MS,
  refetchInterval: SIGNED_URL_REFRESH_MS,
  retry: false,
} as const;

interface MediaUrlResolverValue {
  scope: string;
  resolve: (key: string) => Promise<string>;
}

const MediaUrlResolverContext = createContext<MediaUrlResolverValue | null>(null);

interface MediaUrlResolverProviderProps extends MediaUrlResolverValue {
  children: ReactNode;
}

/**
 * Pages decide how a stored media key becomes a viewable URL (staff session or form-link
 * token); the form fields only ask. `scope` keeps URLs resolved for one route from being
 * reused by another.
 */
export function MediaUrlResolverProvider({
  scope,
  resolve,
  children,
}: MediaUrlResolverProviderProps) {
  const value = useMemo(() => ({ scope, resolve }), [scope, resolve]);
  return (
    <MediaUrlResolverContext.Provider value={value}>{children}</MediaUrlResolverContext.Provider>
  );
}

export const useHasMediaUrlResolver = (): boolean => useContext(MediaUrlResolverContext) !== null;

export function useResolvedMediaUrl(key: string): string | undefined {
  const resolver = useContext(MediaUrlResolverContext);
  const { data } = useQuery({
    queryKey: ["formMediaUrl", resolver?.scope, key],
    queryFn: () =>
      resolver ? resolver.resolve(key) : Promise.reject(new Error("No media URL resolver")),
    enabled: resolver !== null,
    ...SIGNED_MEDIA_URL_QUERY_TIMING,
  });
  return data;
}
